import { useTranslation } from 'react-i18next';
import { IconMenu } from '@/components/icons/Icons';
import { useAuth } from '@/hooks/useAuth';
import { Avatar } from '@/components/common/Avatar';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import styles from './TopBar.module.css';

interface TopBarProps {
  /** Page or section title displayed in the app bar. */
  title: string;
  /** Optional callback for the mobile hamburger menu toggle. */
  onMenuClick?: () => void;
}

/**
 * Top app bar with title, notification bell, and user avatar with sign-out.
 * Uses sticky positioning so content scrolls beneath it.
 */
export function TopBar({ title, onMenuClick }: TopBarProps) {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();

  return (
    <header className={styles.topBar}>
      <div className={styles.startSection}>
        {onMenuClick && (
          <button
            className={styles.menuButton}
            onClick={onMenuClick}
            aria-label="Toggle navigation menu"
            type="button"
          >
            <IconMenu />
          </button>
        )}
        <h1 className={styles.title}>{title}</h1>
      </div>

      <div className={styles.endSection}>
        {/* Notification bell */}
        <NotificationBell />

        {/* User avatar — click to sign out */}
        {user && (
          <button
            className={styles.avatarButton}
            onClick={logout}
            title={t('actions.sign_out')}
            type="button"
          >
            <Avatar
              src={user.avatarUrl ?? undefined}
              name={user.displayName ?? user.email ?? 'U'}
              size="small"
            />
          </button>
        )}
      </div>
    </header>
  );
}
